import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
    CreateVehicleRequest,
    ListVehiclesParams,
    UpdateVehicleRequest,
    VehicleStatus,
} from '@/lib/api/vehicle-api'
import { vehicleApi } from '@/lib/api/vehicle-api'
import { cameraLiveCircuit } from '@/lib/camera-live-circuit'
import { m } from '@/lib/paraglide/messages'
import { formatApiError } from '@/lib/format-api-error'

function showSaveError(err: unknown) {
    const { title, description } = formatApiError(err, m.common_save_error())
    toast.error(title, { description })
}

export const vehicleKeys = {
    all: ['vehicles'] as const,
    lists: () => [...vehicleKeys.all, 'list'] as const,
    list: (params: ListVehiclesParams) =>
        [...vehicleKeys.lists(), params] as const,
    detail: (id: string) => [...vehicleKeys.all, 'detail', id] as const,
    available: () => [...vehicleKeys.all, 'available'] as const,
}

export function useVehicles(params?: ListVehiclesParams) {
    return useQuery({
        queryKey: vehicleKeys.list(params ?? {}),
        queryFn: async () => {
            const res = await vehicleApi.list(params)
            return { data: res.data ?? [], meta: res.meta }
        },
    })
}

export function useAvailableVehicles(enabled = true) {
    return useQuery({
        queryKey: vehicleKeys.available(),
        queryFn: async () => {
            const res = await vehicleApi.available()
            return res.data ?? []
        },
        enabled,
    })
}

export function useVehicle(id: string) {
    return useQuery({
        queryKey: vehicleKeys.detail(id),
        queryFn: async () => {
            const res = await vehicleApi.getById(id)
            return res.data ?? null
        },
        enabled: Boolean(id),
    })
}

export function useCreateVehicle() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: CreateVehicleRequest) => vehicleApi.create(data),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: vehicleKeys.lists() })
            void qc.invalidateQueries({ queryKey: vehicleKeys.available() })
            toast.success(m.vehicle_toast_created())
        },
        onError: showSaveError,
    })
}

export function useUpdateVehicle(id: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: UpdateVehicleRequest) => vehicleApi.update(id, data),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: vehicleKeys.lists() })
            void qc.invalidateQueries({ queryKey: vehicleKeys.detail(id) })
            void qc.invalidateQueries({ queryKey: vehicleKeys.available() })
            // The admin may have just fixed the IMEI/camera config that
            // opened a kind=config circuit for this vehicle — the station
            // screen should retry immediately, not wait for a manual click.
            cameraLiveCircuit.recordSuccess(id)
            toast.success(m.vehicle_toast_updated())
        },
        onError: showSaveError,
    })
}

export function useDeleteVehicle() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => vehicleApi.delete(id),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: vehicleKeys.lists() })
            void qc.invalidateQueries({ queryKey: vehicleKeys.available() })
            toast.success(m.vehicle_toast_deleted())
        },
        onError: () => toast.error(m.common_save_error()),
    })
}

export function useSetVehicleStatus() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, status }: { id: string; status: VehicleStatus }) =>
            vehicleApi.setStatus(id, status),
        onSuccess: (_res, vars) => {
            void qc.invalidateQueries({ queryKey: vehicleKeys.lists() })
            void qc.invalidateQueries({
                queryKey: vehicleKeys.detail(vars.id),
            })
            void qc.invalidateQueries({ queryKey: vehicleKeys.available() })
            toast.success(m.vehicle_toast_status_updated())
        },
        onError: () => toast.error(m.common_save_error()),
    })
}
